import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../services/apiService';
import './SetupAgentPage.css';
import './ViewAgentPage.css';
import './AttributeRegistryPage.css';
import { 
  getAvailableEnvironments, 
  getSelectedEnvironment, 
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';


const ArchiveManagementPage = () => {
  const [instances, setInstances] = useState([]);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [archiveData, setArchiveData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [archiveForm, setArchiveForm] = useState({
      instanceId: '',
      archiveId: '',
      archiveName: '',
      backupDate: '',
      resourceCount: 0,
      healthStatus: '',
  })
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [viewedArchive, setViewedArchive] = useState(null);
  const [viewedDocument, setViewedDocument] = useState(null);
  const [expandedComponent, setExpandedComponent] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState(null);

  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  // Get API endpoints based on selected environment
  const apiEndpoints = getApiEndpoints(selectedEnvironment);
  const INSTANCE_DROPDOWN_API = apiEndpoints.instanceDropdown;

  const getUserRole = () => (
    JSON.parse(localStorage.getItem('user') || 'null')?.role || ''
  ); 

  // Handle environment change
  const handleEnvironmentChange = (e) => {
    const newEnvironment = e.target.value;
    setSelectedEnvironment(newEnvironment);
    saveSelectedEnvironment(newEnvironment);
    setResult(null);
    setError(null);
  };

 const handleInstanceChange = (e) => {
    const instanceId = e.target.value;
    setError(null);
    setResult(null);

    if (instanceId) {
      fetchArchiveData(instanceId);
    } 
  };

  // Archive records come from two different sources with different shapes:
  //  - the real view_archives Lambda returns DynamoDB metadata items
  //    ({date, resources: <count>, resourceCounts, status, s3Key})
  //  - local mock fixtures may instead be full S3 archive documents
  //    ({createdAt, resources: {component: [...]}, no status/resourceCounts})
  // Normalize both into the shape the rest of this page expects.
  const normalizeArchiveRecord = (archive) => {
    if (!archive) return archive;
    const date = archive.date || '';
    const resourcesIsMap = archive.resources && typeof archive.resources === 'object' && !Array.isArray(archive.resources);

    const resourceCounts = Array.isArray(archive.resourceCounts)
      ? archive.resourceCounts
      : resourcesIsMap
        ? Object.entries(archive.resources).map(([component, items]) => ({
            component,
            label: component,
            count: Array.isArray(items) ? items.length : 0,
          }))
        : [];

    const resourceCount = resourcesIsMap
      ? resourceCounts.reduce((sum, entry) => sum + (entry.count || 0), 0)
      : (typeof archive.resources === 'string' || typeof archive.resources === 'number' ? archive.resources : 0);

    return {
      ...archive,
      date,
      resources: resourceCount,
      resourceCounts,
      status: archive.status || 'Complete',
    };
  };

  const fetchArchiveData = async (instanceId) => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await apiService.post(apiEndpoints.archiveDataApi, {
        instanceId: instanceId,
        user_role: getUserRole()
      });
      if (response.data.status === 'ok') {
        const rawArchiveData = Array.isArray(response.data.result.archiveData) ? response.data.result.archiveData : [];
        const fetchedArchiveData = rawArchiveData.map(normalizeArchiveRecord);
        // latest backup is the entry with the most recent date
        const latestArchive = fetchedArchiveData.reduce((latest, archive) => {
          if (!latest) return archive;
          return new Date(archive.date) > new Date(latest.date) ? archive : latest;
        }, null);
        setArchiveForm((prev) => ({
          ...prev,
          instanceId,
          archiveId: latestArchive?.archiveId || '',
          backupDate: latestArchive?.date || '',
          resourceCount: latestArchive?.resources || 0,
          healthStatus: latestArchive ? getArchiveStatusLabel(latestArchive) : '',
        }));
        setArchiveData(fetchedArchiveData);
      } else {
        setError(response.data.message || 'Failed to fetch archive data');
        setArchiveData([]);
      }
    } catch (err) {
      setError(err.message || 'Error fetching archive data');
      setArchiveData([]);
    } finally {
      setLoading(false);
    }
  };


const getArchiveStatusLabel = (archive) => {
  const status = archive.status ?? archive.active;
  if (typeof status === 'boolean') return status ? 'Complete' : 'Error';
  return String(status || '').toLowerCase() === 'error' ? 'Error' : 'Complete';
};

// eslint-disable-next-line react-hooks/exhaustive-deps
const getSortValue = (archive, key) => {
  if (key === 'status') {
    return getArchiveStatusLabel(archive).toLowerCase();
  }
  return String(archive[key] ?? '').toLowerCase();
};

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };  

  const sortIndicator = (key) => (sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');

  const filteredArchiveData = React.useMemo(() => {
      const term = searchTerm.trim().toLowerCase();
      if (!term) return archiveData;
      return archiveData.filter((archive) => {
        return [
          archive.data,
          getArchiveStatusLabel(archive),
          archive.resources,
          archive.instanceId,
        ].some((value) => String(value || '').toLowerCase().includes(term));
      });
    }, [archiveData, searchTerm]);

  const sortedArchiveData = React.useMemo(() => {
      if (!sortKey) return filteredArchiveData;
      const copy = [...filteredArchiveData];
      copy.sort((a, b) => {
        const va = getSortValue(a, sortKey);
        const vb = getSortValue(b, sortKey);
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
      return copy;
    }, [filteredArchiveData, sortKey, sortDir, getSortValue]);

  const totalPages = Math.max(1, Math.ceil(sortedArchiveData.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedArchiveData = sortedArchiveData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  }; 


  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Any';
    const date = new Date(timestamp);
    return date.toDateString().substring(4) + ' ' + date.toLocaleTimeString();
  };

  const viewArchive = async (archive) => {
    setViewLoading(true);
    setViewError(null);
    setViewedDocument(null);
    setExpandedComponent(null);
    setViewedArchive(archive);
    try {
      const response = await apiService.post(apiEndpoints.archiveDetailApi, {
        instanceId: archive.instanceId,
        archiveId: archive.archiveId,
        archiveName: archive.archiveName,
      });
      if (response.data.status === 'ok') {
        response.data.result?.document && 
            (response.data.result.document.createdAt = formatTimestamp(response.data.result.document.createdAt));
        setViewedDocument(response.data.result?.document || null);
      } else {
        setViewError(response.data.message || 'Failed to load archive detail');
      }
    } catch (err) {
      setViewError(err.message || 'Error loading archive detail');
    } finally {
      setViewLoading(false);
    }
  };

  const closeArchiveView = () => {
    setViewedArchive(null);
    setViewedDocument(null);
    setExpandedComponent(null);
    setViewError(null);
  };

  const downloadArchiveDocument = () => {
    if (!viewedDocument) return;
    const blob = new Blob([JSON.stringify(viewedDocument, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${viewedArchive?.archiveId || 'archive'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // const compareArchive = (archive) => {
  //   // Implement the logic to compare the archive
  //   console.log('Comparing archive:', archive);
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);
  };

  // Fetch Connect instances on component mount and when environment changes
  useEffect(() => {
    const fetchInstances = async () => {
      setLoadingInstances(true);
      try {
        const response = await apiService.get(INSTANCE_DROPDOWN_API);
        if (response.data.status === 'ok') {
          setInstances(response.data.instances || []);
        } else {
          console.error('Failed to load instances:', response.data.message);
        }
      } catch (err) {
        console.error('Error fetching instances:', err);
      } finally {
        setLoadingInstances(false);
      }
    };
    fetchInstances();
  }, [INSTANCE_DROPDOWN_API, selectedEnvironment]);

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
            <h1>Manage Connect Archives</h1>
              <p className="page-subtitle">Manage Connect archive and restore operations</p>
        </div>
        <Link to="/business-admin" className="btn btn-secondary">
          ← Back to Business Administration
        </Link>
      </div>

      {/* Environment Selector */}
      <div className="environment-selector" style={{
        backgroundColor: '#f9f9f9',
        border: '2px solid #e0e0e0',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <label htmlFor="environment" style={{ 
          fontWeight: 'bold', 
          fontSize: '16px',
          minWidth: '120px'
        }}>
          Environment:
        </label>
        <select
          id="environment"
          value={selectedEnvironment}
          onChange={handleEnvironmentChange}
          style={{
            padding: '10px 16px',
            fontSize: '15px',
            borderRadius: '6px',
            border: '2px solid #ccc',
            backgroundColor: 'white',
            cursor: 'pointer',
            fontWeight: '500',
            minWidth: '200px'
          }}
        >
          {availableEnvironments.map(env => (
            <option key={env} value={env}>
              {ENVIRONMENT_LABELS[env]} ({env})
            </option>
          ))}
        </select>
        <span style={{
          padding: '6px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 'bold',
          color: 'white',
          backgroundColor: getEnvironmentColor(selectedEnvironment)
        }}>
          {selectedEnvironment}
        </span>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-section">
            <h3>Connect Archive Management</h3>

            <div className="form-group">
                <label htmlFor="instanceId">Instance *</label>
                <select
                  id="instanceId"
                  value={archiveForm.instanceId}
                  onChange={handleInstanceChange}
                  required
                  disabled={loadingInstances}
                >
                  <option value="">
                    {loadingInstances ? 'Loading instances...' : 'Select an instance...'}
                  </option>
                  {instances.map((instance) => (
                    <option key={instance.id} value={instance.id}>
                      {instance.name} ({instance.status})
                    </option>
                  ))}
                </select>
                <small>Select an Amazon Connect instance</small>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="routingPolicy">Last Backup</label>
                <input
                  id="backupDate"
                  value={archiveForm.backupDate
                    ? `${formatTimestamp(archiveForm.backupDate)}`
                    : ''}
                  readOnly
                  placeholder="Backup Date"
                />
              </div>
               <div className="form-group">
                <label htmlFor="resourceCount">Resources</label>
                <input
                  id="resourceCount"
                  value={archiveForm.resourceCount
                    ? `${archiveForm.resourceCount}`
                    : ''}
                  readOnly
                  placeholder="Resource Count"
                />
              </div>
               <div className="form-group">
                <label htmlFor="healthStatus">Health</label>
                <input
                  id="healthStatus"
                  value={archiveForm.healthStatus
                    ? `${archiveForm.healthStatus}`
                    : ''}
                  readOnly
                  placeholder="Health Status"
                />
              </div>

              {!loading && (
                <>
                  <Link
                    to={archiveForm.instanceId ? `/create-archive?instanceId=${archiveForm.instanceId}` : '#'}
                    className="btn btn-primary"
                    aria-disabled={!archiveForm.instanceId}
                    onClick={(e) => { if (!archiveForm.instanceId) e.preventDefault(); }}
                    style={!archiveForm.instanceId ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    Create Archive
                  </Link>
                  <Link
                    // to={archiveForm.instanceId ? `/compare-archives?instanceId=${archiveForm.instanceId}` : '#'}
                    // className="btn btn-primary"
                    // disabled={true}
                    // onClick={(e) => { if (!archiveForm.instanceId) e.preventDefault(); }}
                    // style={!archiveForm.instanceId ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    to="#"
                    className="btn btn-primary"
                    aria-disabled="true"
                    onClick={(e) => e.preventDefault()}
                    style={{ pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    Compare Archives
                  </Link>
                  <Link
                    // to={archiveForm.instanceId ? `/restore-archive?instanceId=${archiveForm.instanceId}` : '#'}
                    // className="btn btn-primary"
                    // disabled={true}
                    // onClick={(e) => { if (!archiveForm.instanceId) e.preventDefault(); }}
                    // style={!archiveForm.instanceId ? { pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' } : undefined}
                    to="#"
                    className="btn btn-primary"
                    aria-disabled="true"
                    onClick={(e) => e.preventDefault()}
                    style={{ pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    Restore Archive
                  </Link>
                </>
              )}

            </div>        
          </div>
        </form>
      </div>

      {archiveData.length > 0 && (
        <div className="table-format-list">
          <div className="table-format-list-header">
            <h3>Backup History({archiveData.length}{sortedArchiveData.length !== archiveData.length ? ` of ${archiveData.length}` : ''})</h3>
            <div className="table-format-list-controls">
              <input
                type="text"
                className="table-format-search-input"
                placeholder="Search Routing Policies..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
              />
              <div className="page-size-selector">
                <label htmlFor="page_size">Rows per page:</label>
                <select id="page_size" value={pageSize} onChange={handlePageSizeChange}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('archiveName')}>Archive Name{sortIndicator('archiveName')}</th>
                <th className="sortable" onClick={() => handleSort('archiveId')}>Archive ID{sortIndicator('archiveId')}</th>
                <th className="sortable" onClick={() => handleSort('instanceId')}>Instance{sortIndicator('instanceId')}</th>
                <th className="sortable" onClick={() => handleSort('date')}>Date{sortIndicator('date')}</th>
                <th className="sortable" onClick={() => handleSort('status')}>Status{sortIndicator('status')}</th>
                <th className="sortable" onClick={() => handleSort('resources')}>Resources{sortIndicator('resources')}</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedArchiveData.map((archive, index) => (
                <tr key={`${archive.archiveId || archive.instanceId}-${index}`}>
                  <td>{archive.archiveName || '-'}</td>
                  <td>{archive.archiveId || '-'}</td>
                  <td>{archive.instanceId || '-'}</td>
                  <td>{formatTimestamp(archive.date) || '-'}</td>
                  <td>{getArchiveStatusLabel(archive) || '-'}</td>
                  <td>{archive.resources || 0} </td>
                   <td>
                    <button type="button"className="btn btn-secondary" onClick={() => viewArchive(archive)}>
                      View
                    </button>
                    {/* <button type="button" className="btn btn-danger" onClick={() => compareArchive(archive)} style={{marginLeft: '4px'}}>
                      Compare
                    </button> */}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination-controls">
              <button type="button" className="btn btn-secondary" onClick={() => setPage(1)} disabled={currentPage === 1}>« First</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              <span className="pagination-status">Page {currentPage} of {totalPages}</span>
              <button type="button" className="btn btn-secondary" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <button type="button" className="btn btn-secondary" onClick={() => setPage(totalPages)} disabled={currentPage === totalPages}>Last »</button>
            </div>
        </div>
      )}

       {error && (
        <div className="alert alert-error">
          <h4>Error</h4>
          <p>{error}</p>
        </div>
      )}
      {result && (
        <div className="alert alert-success">
          <h4>Success</h4>
          <p>{result.message}</p>
        </div>
      )}

      {viewedArchive && (
        <div className="archive-detail-modal-overlay" onClick={closeArchiveView}>
          <div className="archive-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="archive-detail-modal-header">
              <h3>Archive: {viewedArchive.archiveName}</h3>
              <button type="button" className="btn btn-secondary" onClick={closeArchiveView}>Close</button>
            </div>

            <div className="archive-detail-summary">
              <span><strong>Instance:</strong> {viewedArchive.instanceId}</span>
              <span><strong>ArchiveId:</strong> {viewedArchive.archiveId || '-'}</span>
              <span><strong>Date:</strong> {formatTimestamp(viewedArchive.date)}</span>
              <span><strong>Type:</strong> {viewedArchive.archiveType || 'FULL'}</span>
              <span><strong>Status:</strong> {getArchiveStatusLabel(viewedArchive)}</span>
              <span><strong>Total Resources:</strong> {viewedArchive.resources || 0}</span>
            </div>

            {viewLoading && <p>Loading archive detail...</p>}
            {viewError && (
              <div className="alert alert-error">
                <h4>Error</h4>
                <p>{viewError}</p>
              </div>
            )}

            {!viewLoading && !viewError && viewedDocument && (
              <>
                <table className="archive-resource-counts">
                  <thead>
                    <tr>
                      <th>Resource Type</th>
                      <th>Count</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(viewedDocument.resources || {}).map((component) => {
                      const items = viewedDocument.resources[component] || [];
                      const isExpanded = expandedComponent === component;
                      return (
                        <React.Fragment key={component}>
                          <tr>
                            <td>{component}</td>
                            <td>{items.length}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setExpandedComponent(isExpanded ? null : component)}
                              >
                                {isExpanded ? 'Hide' : 'Show'}
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={3}>
                                <ul className="archive-detail-item-list">
                                  {items.map((item, idx) => (
                                    <li key={item.Id || item.id || idx}>
                                      {item.Name || item.Username || item.Id || `Item ${idx + 1}`}
                                    </li>
                                  ))}
                                  {items.length === 0 && <li>No resources of this type were found.</li>}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <div className="archive-detail-modal-actions">
                  <button type="button" className="btn btn-primary" onClick={downloadArchiveDocument}>
                    Download Full Archive (JSON)
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default ArchiveManagementPage;